import React, { createContext, useState, Dispatch, SetStateAction, useContext, useMemo } from 'react';
import { DRAWING_TOOL_COLORS } from '../constants';
import { UndoRedoAction, Type } from '../hooks/useUndoRedo';
import { LineType } from '../types';
import { UndoRedoContext } from './undoRedoContext';

export interface IDrawingContext {
  lines: Record<string, Array<LineType>>;
  isPen: boolean;
  areDrawingsHidden: boolean;
  drawingColor: string;
  setLines: Dispatch<SetStateAction<Record<string, LineType[]>>>;
  setPen: Dispatch<SetStateAction<boolean>>;
  setDrawingColor: Dispatch<SetStateAction<string>>;
  hideDrawings: () => void;
  setDrawingsHidden: Dispatch<SetStateAction<boolean>>;
  clearDrawings: (page: string) => void;
  saveDrawingUpdates: (prevState: IDrawingContext['lines'], state: IDrawingContext['lines']) => void;
  saveDrawingsHiddenUpdates: (prevState: boolean, state: boolean) => void;
}

const initialState = {
  lines: {},
  isPen: false,
  drawingColor: '',
  areDrawingsHidden: false,
  setLines: (): void => {
    console.error('Function should be provided through the context value');
  },
  setPen: (): void => {
    console.error('Function should be provided through the context value');
  },
  setDrawingColor: (): void => {
    console.error('Function should be provided through the context value');
  },
  hideDrawings: () => null,
  setDrawingsHidden: () => null,
  clearDrawings: () => null,
  saveDrawingUpdates: () => null,
  saveDrawingsHiddenUpdates: () => null,
};

export const DrawingContext = createContext<IDrawingContext>(initialState);

export const DrawingContextProvider: React.FC = ({ children }): JSX.Element => {
  const [lines, setLines] = useState<Record<string, Array<LineType>>>({ words: [] });
  const [isPen, setPen] = useState<boolean>(false);
  const [drawingColor, setDrawingColor] = useState<string>(DRAWING_TOOL_COLORS[0]);
  const [areDrawingsHidden, setDrawingsHidden] = useState(false);
  const { registerUndoRedoCreator } = useContext(UndoRedoContext);

  const hideDrawings = () => {
    setDrawingsHidden(!areDrawingsHidden);
  };

  const clearDrawings = (page: string) => {
    setLines({ [page]: [] });
    setPen(false);
    setDrawingColor(DRAWING_TOOL_COLORS[0]);
    setDrawingsHidden(false);
  };

  const saveDrawingUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.pen, (payload: UndoRedoAction) => {
        setLines(payload as IDrawingContext['lines']);
      }),
    []
  );

  const saveDrawingsHiddenUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.hideDrawings, (payload: UndoRedoAction) => {
        setDrawingsHidden(payload as boolean);
      }),
    []
  );

  return (
    <DrawingContext.Provider
      value={{
        lines,
        setLines,
        isPen,
        setPen,
        drawingColor,
        setDrawingColor,
        areDrawingsHidden: areDrawingsHidden,
        hideDrawings: hideDrawings,
        setDrawingsHidden,
        clearDrawings,
        saveDrawingUpdates,
        saveDrawingsHiddenUpdates,
      }}
    >
      {children}
    </DrawingContext.Provider>
  );
};
